import { useState } from "react"

import { ADDER_CATEGORY } from "@logic/lib/powur-os"
import { clsx } from "clsx"

import {
  AllAdders,
  Battery,
  Electrical,
  Generators,
  Misc,
  Mounting,
  ProjectService,
  Roofing,
  Travel,
} from "~/components/Icons/adderIcons"
import { TextField } from "~/components/ui-components/v2"
import { formatCurrency } from "~/utils/finance/format"

import type {
  AdderCatalog,
  AdderDraft,
  AdderDraftMap,
  PricingAdder,
  PricingAddersUpdate,
  PricingKindBundle,
} from "./pricing-kind-bundle"
import {
  CardField,
  PricingLoadError,
  PricingLoading,
  PricingStepDrawer,
  PricingStepFooter,
  ToggleCard,
  ToggleCardGrid,
  ToggleCardTitle,
} from "./pricing-step-chrome"
import { usePricingSaveGuard } from "./use-pricing-save-guard"

const ALL_ADDERS_ID = ADDER_CATEGORY[`All Adders`]

const CATEGORY_ICONS: Record<number, typeof AllAdders> = {
  [ADDER_CATEGORY[`All Adders`]]: AllAdders,
  [ADDER_CATEGORY.Electrical]: Electrical,
  [ADDER_CATEGORY.Mounting]: Mounting,
  [ADDER_CATEGORY.Miscellaneous]: Misc,
  [ADDER_CATEGORY[`Base Services`]]: ProjectService,
  [ADDER_CATEGORY[`Roof Type`]]: Roofing,
  [ADDER_CATEGORY[`Ground Mounts`]]: Mounting,
  [ADDER_CATEGORY[`Battery Installation`]]: Battery,
  [ADDER_CATEGORY.Generators]: Generators,
  [ADDER_CATEGORY.Travel]: Travel,
}

const UNSELECTED: AdderDraft = {
  selected: false,
  customPrice: null,
  customQuantity: null,
}

const draftFor = (adder: PricingAdder, draft: AdderDraftMap): AdderDraft =>
  draft[adder.id] ?? { ...UNSELECTED, selected: adder.required }

/** Every adder with its draft state; the server forces required ones on. */
export const addersUpdateFromDraft = (
  catalog: AdderCatalog,
  draft: AdderDraftMap,
): PricingAddersUpdate => ({
  adders: catalog.adders.map((adder) => {
    const state = draft[adder.id]
    const selected = state?.selected ?? adder.required
    return {
      powurOsAdderId: adder.id,
      selected,
      // A variable-price adder prices at quantity 1, whatever quantity it
      // kept from before the catalog made it variable.
      customQuantity: !selected
        ? null
        : adder.quantityMode === `price`
          ? 1
          : (state?.customQuantity ?? adder.defaultQuantity),
      customPrice: selected ? (state?.customPrice ?? null) : null,
    }
  }),
})

/** "× 24 panels": a derived quantity with its unit. */
export const derivedQuantityText = (
  quantity: number,
  unitLabel: string,
): string =>
  `× ${quantity.toLocaleString(`en-US`)} ${quantity === 1 ? unitLabel : `${unitLabel}s`}`

/**
 * The adder draft: toggling, typed quantities and prices, required adders
 * held on, and single-select categories (`allowMultiples: false`) dropping
 * the others when one is chosen.
 */
const nextDraft = (
  catalog: AdderCatalog,
  draft: AdderDraftMap,
  adderId: number,
  next: Partial<AdderDraft>,
): AdderDraftMap => {
  const adder = catalog.adders.find((entry) => entry.id === adderId)
  const required = adder?.required === true
  const exclusiveCategoryId =
    next.selected === true && adder?.category?.allowMultiples === false
      ? adder.category.id
      : null
  const result: Record<number, AdderDraft> = {
    ...draft,
    [adderId]: {
      ...UNSELECTED,
      ...draft[adderId],
      ...next,
      selected: required
        ? true
        : (next.selected ?? draft[adderId]?.selected ?? false),
    },
  }

  if (exclusiveCategoryId != null) {
    for (const other of catalog.adders) {
      if (
        other.id !== adderId &&
        other.category?.id === exclusiveCategoryId &&
        !other.required
      ) {
        result[other.id] = UNSELECTED
      }
    }
  }

  return result
}

const AdderCard = ({
  adder,
  state,
  saving,
  onChange,
}: {
  adder: PricingAdder
  state: AdderDraft
  saving: boolean
  onChange: (next: Partial<AdderDraft>) => void
}): JSX.Element => {
  const disabled = saving || adder.required
  const toggleSelected = (): void => {
    if (disabled) {
      return
    }
    const nextSelected = !state.selected
    onChange({
      selected: nextSelected,
      ...(nextSelected ? {} : { customQuantity: null, customPrice: null }),
    })
  }

  return (
    <ToggleCard
      selected={state.selected}
      disabled={disabled}
      dimmed={saving}
      onToggle={toggleSelected}
      title={<ToggleCardTitle>{adder.title}</ToggleCardTitle>}
    >
      {adder.listPrice != null && adder.quantityMode !== `price` ? (
        <span className="text-[length:var(--step--1)] font-semibold text-bp-accent">
          {`${formatCurrency(adder.listPrice)} / ${adder.unitLabel}`}
        </span>
      ) : null}
      {adder.quantityMode === `derived` && adder.derivedQuantity != null ? (
        <span className="text-[length:var(--step--1)] text-foreground-soft tabular-nums">
          {derivedQuantityText(adder.derivedQuantity, adder.unitLabel)}
        </span>
      ) : null}
      {adder.quantityMode === `price` ? (
        <CardField>
          <TextField
            label="Custom price"
            type="number"
            min={0}
            step={1}
            leadingAddon="$"
            disabled={saving}
            value={state.customPrice ?? ``}
            onChange={(event) => {
              const { value } = event.target
              onChange({
                customPrice: value === `` ? null : Number(value),
                selected: value !== `` && value !== `0`,
              })
            }}
          />
        </CardField>
      ) : null}
      {adder.quantityMode === `typed` ? (
        <CardField>
          <TextField
            label="Quantity"
            type="number"
            min={0}
            step={1}
            trailingAddon={adder.unitLabel}
            disabled={saving}
            value={state.customQuantity ?? ``}
            onChange={(event) => {
              const { value } = event.target
              onChange({
                customQuantity: value === `` ? null : Number(value),
                selected: value !== `` && value !== `0`,
              })
            }}
          />
        </CardField>
      ) : null}
    </ToggleCard>
  )
}

const CategoryNav = ({
  categories,
  selectedCategory,
  onSelect,
}: {
  categories: AdderCatalog[`categories`]
  selectedCategory: number
  onSelect: (categoryId: number) => void
}): JSX.Element => (
  <nav aria-label="Adder categories" className="w-full">
    <ul className="flex list-none flex-wrap gap-1 p-0">
      {[{ id: ALL_ADDERS_ID, name: `All Adders` }, ...categories].map(
        (category) => {
          const CategoryIcon = CATEGORY_ICONS[category.id]
          const isSelected = category.id === selectedCategory
          return (
            <li key={category.id}>
              <button
                type="button"
                aria-pressed={isSelected}
                onClick={() => onSelect(category.id)}
                className={clsx(
                  `flex items-center gap-2 rounded-md px-3 py-2 text-left text-[length:var(--step--1)]`,
                  isSelected
                    ? `bg-bp-accent text-bp-on-accent`
                    : `text-foreground hover:bg-bp-elevated`,
                )}
              >
                {CategoryIcon ? (
                  <CategoryIcon aria-hidden="true" className="size-4 shrink-0" />
                ) : null}
                {category.name}
              </button>
            </li>
          )
        },
      )}
    </ul>
  </nav>
)

/**
 * Adders: category nav over toggle cards, drafted locally and sent whole on
 * Save, which closes the step. A kind may add a section saved by the same
 * Save (Solar's Rebates).
 */
export const PricingAddersStep = ({
  kind,
  proposalId,
  onClose,
}: {
  kind: PricingKindBundle
  proposalId: string
  onClose: () => void
}): JSX.Element => {
  const bundle = kind.adders
  const read = bundle.useAdders(proposalId)
  const financingLock = kind.useFinancingLock(proposalId)
  const saveAdders = bundle.useSave(proposalId)
  const guard = usePricingSaveGuard({ onClose, savedFeedback: false })
  const [userDraft, setUserDraft] = useState<AdderDraftMap | null>(null)
  const [selectedCategory, setSelectedCategory] = useState(ALL_ADDERS_ID)
  const companion = bundle.useCompanion(proposalId, {
    disabled: saveAdders.isLoading,
  })
  const { catalog } = read
  const draft = userDraft ?? catalog?.saved ?? {}
  const listed = catalog?.adders.filter((adder) => adder.listed) ?? []
  const visibleAdders =
    selectedCategory === ALL_ADDERS_ID
      ? listed
      : listed.filter((adder) => adder.category?.id === selectedCategory)
  const saveInFlight = saveAdders.isLoading || companion?.saving === true
  const canSave =
    read.ready &&
    !financingLock.locked &&
    !financingLock.pending &&
    !saveInFlight &&
    companion?.blocked !== true

  const setAdderDraft = (adderId: number, next: Partial<AdderDraft>): void => {
    if (saveInFlight || catalog == null) {
      return
    }
    saveAdders.reset()
    setUserDraft(nextDraft(catalog, draft, adderId, next))
  }

  const submit = (): void => {
    if (!canSave || catalog == null || !guard.start()) return
    // A successful adders save hands the guard on: to the companion's save
    // when it has changes, else to `succeed`, which closes the step.
    let handedOn = false
    saveAdders.run(addersUpdateFromDraft(catalog, draft), {
      onSuccess: () => {
        handedOn = true
        if (companion?.dirty) {
          companion.save({ onSuccess: guard.succeed, onError: guard.release })
          return
        }
        guard.succeed()
      },
      onSettled: () => {
        if (!handedOn) guard.release()
      },
    })
  }

  return (
    <PricingStepDrawer
      title="Adders"
      onClose={onClose}
      contentClassName="flex flex-col"
    >
      {financingLock.notice}
      {read.loading ? <PricingLoading>Loading adders</PricingLoading> : null}
      {read.loadError ? (
        <PricingLoadError message={read.loadError} onRetry={read.retry} />
      ) : null}
      {saveAdders.isError ? (
        <p role="alert" className="text-[length:var(--step--2)] text-danger">
          {bundle.describeSaveError(saveAdders.error)}
        </p>
      ) : null}

      {catalog ? (
        <div className="flex min-h-0 flex-1 flex-col gap-5">
          <CategoryNav
            categories={catalog.categories}
            selectedCategory={selectedCategory}
            onSelect={setSelectedCategory}
          />

          <ToggleCardGrid
            testId="adders-cards-container"
            className="grid min-w-0 flex-1 auto-rows-min grid-cols-2 gap-4"
          >
            {visibleAdders.length === 0 ? (
              <p className="text-[length:var(--step--1)] text-muted">
                No adders available
              </p>
            ) : (
              visibleAdders.map((adder) => (
                <AdderCard
                  key={adder.id}
                  adder={adder}
                  state={draftFor(adder, draft)}
                  saving={saveInFlight}
                  onChange={(next) => setAdderDraft(adder.id, next)}
                />
              ))
            )}
          </ToggleCardGrid>
          {companion?.section}
        </div>
      ) : null}

      <PricingStepFooter
        onClose={onClose}
        error={null}
        savedMessage={null}
        save={{
          label: bundle.copy.saveLabel,
          saving: saveInFlight,
          disabled: !canSave,
          onSave: submit,
        }}
      />
    </PricingStepDrawer>
  )
}
